// Two roles: (a) if a ?token=... is in the URL, hit the API immediately;
// (b) otherwise show the "check your inbox" screen (post-signup).
import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { resendVerification, verifyEmail } from "../api/auth";
import { ApiError } from "../api/client";

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const location = useLocation();
  const signupEmail = (location.state as { email?: string } | null)?.email;

  const [status, setStatus] = useState<"inbox" | "verifying" | "verified" | "failed">(
    token ? "verifying" : "inbox",
  );
  const [error, setError] = useState<string | null>(null);
  const [resentTo, setResentTo] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await verifyEmail(token);
        setStatus("verified");
      } catch (err) {
        setError(err instanceof ApiError ? err.detail : "Verification failed");
        setStatus("failed");
      }
    })();
  }, [token]);

  async function handleResend() {
    if (!signupEmail) return;
    try {
      await resendVerification(signupEmail);
      setResentTo(signupEmail);
    } catch {
      setResentTo(null);
    }
  }

  return (
    <>
      <header className="flex items-center justify-between border-b border-rule-soft px-7 py-5">
        <Link to="/" className="font-display text-2xl tracking-[-0.04em] text-ink">
          Varo
        </Link>
        <Link to="/login" className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-mute hover:text-terra">
          Back to sign in
        </Link>
      </header>

      <main className="grid min-h-[80vh] place-items-center p-7">
        <div className="w-full max-w-xl rounded-lg border border-rule bg-cream-card p-12 text-center shadow-md">
          {status === "inbox" && (
            <>
              <div className="mx-auto mb-7 inline-grid size-20 -rotate-6 place-items-center rounded-full border-2 border-terra font-mono text-[10px] uppercase tracking-[0.18em] text-terra-deep">
                Check
                <br />
                Inbox
              </div>
              <p className="eyebrow eyebrow-accent">03. One more step</p>
              <h1 className="mt-4 mb-5 text-4xl tracking-tight">
                We sent a link to your inbox.
              </h1>
              <p className="text-ink-soft">
                Click the link in the email{signupEmail ? ` to ` : "."}
                {signupEmail && (
                  <strong className="text-ink">{signupEmail}</strong>
                )}
                {signupEmail && " to verify your address."}
              </p>

              <div className="mt-7 border-l-2 border-moss bg-paper-deep p-5 text-left text-xs text-ink-soft">
                <strong>Doesn't arrive in a minute?</strong> Check spam, or any folder your mail
                client routes "no-reply" notifications into. The link expires in 24&nbsp;hours.
              </div>

              {signupEmail && (
                <div className="mt-7 flex flex-wrap items-center justify-center gap-3 border-t border-rule-soft pt-6">
                  <button className="btn btn-secondary text-xs" onClick={handleResend}>
                    Resend email
                  </button>
                  {resentTo && (
                    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-moss">
                      ✓ Sent to {resentTo}
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {status === "verifying" && (
            <>
              <p className="eyebrow">verifying token…</p>
              <h1 className="mt-4 text-3xl tracking-tight">One moment.</h1>
            </>
          )}

          {status === "verified" && (
            <>
              <p className="eyebrow eyebrow-accent">Confirmed</p>
              <h1 className="mt-4 mb-5 text-4xl tracking-tight">
                You're verified.
              </h1>
              <p className="text-ink-soft">
                Your account is{" "}
                <strong className="text-ink">pending admin approval</strong>.
                Varo is invite-only — an admin will review and activate your
                account, then reach out so you can sign in.
              </p>
              <div className="mt-7 border-l-2 border-terra bg-paper-deep p-5 text-left text-xs text-ink-soft">
                <strong>What happens next?</strong> An admin gets notified, they
                approve your account, and you'll hear back from them with the
                go-ahead to sign in. No further action needed from you right
                now.
              </div>
            </>
          )}

          {status === "failed" && (
            <>
              <p className="eyebrow text-plum">Couldn't verify</p>
              <h1 className="mt-4 mb-5 text-3xl tracking-tight">This link is invalid or expired.</h1>
              <p className="text-ink-soft">{error}</p>
              <div className="mt-7 flex justify-center gap-3">
                <Link to="/login" className="btn btn-secondary">
                  Go to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </>
  );
}
